import { unwrapResult } from '@reduxjs/toolkit';
import {
  ClaimProposalDTOStatusEnum,
  ClaimWithCheckoutRelationsDTO,
  ClaimWithCheckoutRelationsDTOStatusEnum,
  EvidenceChaserDTOStatusEnum,
  EvidenceChaserDTOTypeEnum,
  OrderWithOrderCustomersDTOProductIdEnum,
  OrderWithOrderCustomersDTORefundHandlingEnum,
  PaymentPlanDTOStatusEnum,
  PaymentPlanInstalmentDTOStatusEnum,
  PaymentPlanInstalmentReschedulePreviewDTO,
  UserDTOInternalRolesEnum,
} from '@reposit/api-client';
import { Formik } from 'formik';
import { get, uniqBy } from 'lodash';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Col, Container, Row } from 'react-grid-system';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import * as Yup from 'yup';
import { useAppDispatch } from '../../..';
import poundIcon from '../../../assets/svg/pound-sterling.svg';
import { RepositTheme } from '../../../base/theme';
import Button from '../../../components/Button';
import ClaimArbitration from '../../../components/ClaimArbitration';
import ClaimDetails from '../../../components/ClaimDetails';
import ClaimDocuments from '../../../components/ClaimDocuments';
import ClaimFinancialSummary from '../../../components/ClaimFinancialSummary';
import ClaimItems from '../../../components/ClaimItems';
import ClaimItemEditModal from '../../../components/ClaimItems/ClaimItemEditModal';
import { MediationClaimItems } from '../../../components/ClaimItems/MediationClaimItems';
import DisputeInformation from '../../../components/ClaimResponse';
import ClaimReview from '../../../components/ClaimReview';
import { ClaimStatuses } from '../../../components/ClaimStatuses';
import { ConfirmationModalProps, NotificationInfo, useConfirmationModal } from '../../../components/ConfirmationModal';
import CreateEvidenceChasersModal from '../../../components/CreateEvidenceChasersModal';
import DeadlinesCard from '../../../components/DeadlinesCard';
import DeleteClaimModal from '../../../components/DeleteClaimModal';
import DeletePaymentPlanModal from '../../../components/DeletePaymentPlanModal';
import DownloadClaimDocumentsModal from '../../../components/DownloadClaimDocumentsModal';
import { ExtendResponseDeadlineModal } from '../../../components/ExtendResponseDeadlineModal';
import FlashMessage from '../../../components/FlashMessage';
import { Input } from '../../../components/FormFields';
import DatePicker from '../../../components/FormFields/DatePicker';
import FieldWithLabel from '../../../components/FormFields/FieldWithLabel';
import InventoryDetails from '../../../components/InventoryDetails';
import Loading from '../../../components/Loading';
import Modal from '../../../components/Modal';
import { PaymentPlan } from '../../../components/PaymentPlan/payment-plan';
import PaymentPlanInstalmentDesignerModal from '../../../components/PaymentPlanInstalmentDesignerModal';
import PaymentPlanModal, { HISTORIC_PAYMENT_PLAN, PaymentPlanFormValues } from '../../../components/PaymentPlanModal';
import { ReopenModal, ReopenType } from '../../../components/ReopenModal';
import RepositCard from '../../../components/Reposit/RepositCard';
import RepositSummaryCard from '../../../components/Reposit/RepositSummaryCard';
import { Caption, Header2, Header3, P1 } from '../../../components/Typography';
import WaiveClaimDisputeFeeModal from '../../../components/WaiveClaimDisputeFeeModal';
import { DATE_FORMAT } from '../../../constants/dates';
import { useAdminDocumentTitle } from '../../../hooks/use-document-title';
import { hasInternalRole } from '../../../redux/account/account.selectors';
import {
  CREATE_ARBITRATION_STORE_KEY,
  CREATE_EVIDENCE_CHASERS_STORE_KEY,
  clearDeleteClaimPreview,
  createArbitrationApiRequested,
  DEACTIVATE_CLAIM_STORE_KEY,
  DELETE_CLAIM_STORE_KEY,
  DOWNLOAD_DOCUMENTS_STORE_KEY,
  deactivateClaimApiRequested,
  deleteClaimRequested,
  downloadDocumentsApiRequested,
  EXPIRE_EVIDENCE_CHASER_STORE_KEY,
  expireEvidenceChaserApiRequested,
  FETCH_CLAIM_STORE_KEY,
  fetchClaimRequested,
  GENERATE_ADR_FORM_STORE_KEY,
  OPEN_EVIDENCE_WORKSPACE_STORE_KEY,
  PREVIEW_DELETE_CLAIM_STORE_KEY,
  previewDeleteClaimRequested,
  REOPEN_CLAIM_STORE_KEY,
  RESOLVE_CLAIM_STORE_KEY,
  reopenClaimApiRequested,
  resolveClaimApiRequested,
  SET_PAID_AT_CLAIM_STORE_KEY,
  SKIP_EVIDENCE_CHASERS_STORE_KEY,
  setCurrentClaimActionModal,
  setPaidAtApiRequested,
  skipEvidenceChasersApiRequested,
  UPDATE_AUTO_CHARGE_STORE_KEY,
  updateAutoChargeApiRequested,
  WAIVE_CLAIM_DISPUTE_FEE_STORE_KEY,
} from '../../../redux/claim/claim.actions';
import { getClaimDeletionPreview, getClaimHasBeenFetched } from '../../../redux/claim/claim.selector';
import {
  createEvidenceChasersThunk,
  fetchBankTransactionsEnabledAtEligibilityThunk,
  extendResponseDeadlineThunk,
  generateADRFormThunk,
  openEvidenceWorkspaceThunk,
  previewResponseDeadlineExtensionThunk,
  waiveClaimDisputeFeeThunk,
} from '../../../redux/claim/claim.thunk';
import {
  clearClaimItemPreview,
  INTERNAL_UPDATE_CLAIM_ITEM_STORE_KEY,
  internalUpdateClaimItemRequested,
  PREVIEW_CLAIM_ITEM_UPDATE_STORE_KEY,
  previewClaimItemUpdateRequested,
} from '../../../redux/claim-item/claim-item.actions';
import { createErrorMessageSelector } from '../../../redux/error/error.selector';
import { createLoadingSelector } from '../../../redux/loading/loading.selector';
import { clearAmountRemaining } from '../../../redux/order/order.action';
import { getOrderAmountRemaining } from '../../../redux/order/order.selector';
import {
  fetchAmountRemainingDetailsForOrderThunk,
  fetchAmountRemainingForPaymentPlanOrderThunk,
} from '../../../redux/order/order.thunk';
import {
  DELETE_PAYMENT_PLAN_INSTALMENT_STORE_KEY,
  PAYMENT_PLAN_INSTALMENT_DESIGNER_STORE_KEY,
  RESCHEDULE_PAYMENT_PLAN_INSTALMENT_STORE_KEY,
  UPDATE_PAYMENT_PLAN_TENANT_DEFERRAL_STORE_KEY,
} from '../../../redux/payment-plan/payment-plan.actions';
import {
  getManualPaymentsByClaimId,
  getPaymentPlanByClaimId,
  getPaymentPlanCustomerByClaimId,
  getPendingPaymentPlanOrder,
} from '../../../redux/payment-plan/payment-plan.selectors';
import {
  applyPaymentPlanInstalmentDesignerCsvThunk,
  createPaymentPlanThunk,
  deletePaymentPlanInstalmentThunk,
  deletePaymentPlanThunk,
  downloadPaymentPlanInstalmentDesignerCsvThunk,
  forceDeletePaymentPlanThunk,
  previewPaymentPlanInstalmentDesignerCsvThunk,
  previewPaymentPlanInstalmentRescheduleThunk,
  reschedulePaymentPlanInstalmentThunk,
  updatePaymentPlanTenantDeferralThunk,
} from '../../../redux/payment-plan/payment-plan.thunk';
import { PaymentPlanInstalmentDesignerPreview } from '../../../redux/payment-plan/payment-plan.types';
import {
  FETCH_TENANCY_ORDER_NOTES_STORE_KEY,
  fetchTenancyOrderNotesRequested,
  fetchTenancyOrderRequested,
} from '../../../redux/reposit/reposit.actions';
import { AppState } from '../../../redux/root.reducer';
import { getFullClaimById } from '../../../redux/selectors/claim.selectors';
import { getPendingClaimItemPreview } from '../../../redux/selectors/claim-item.selectors';
import {
  getIsAgentDeciding,
  getIsTenantDeciding,
  getMediationClaimItemsByClaimId,
} from '../../../redux/selectors/mediation.selectors';
import { getTenancyOrder, getTenancyOrderNotes } from '../../../redux/selectors/tenancyOrder.selectors';
import { getTenancyInventoryByClaimId } from '../../../redux/tenancy-inventory/tenancy-inventory.selectors';
import { getClaimStatus, getDisplayAddress } from '../../../utils/common.utils';
import { twoDPRegex } from '../../../utils/regex/number.regex';
import { FLASH_MESSAGE_TIMEOUT, useFlashMessage } from '../../FlashMessage';
import ClaimManualPayments from '../ManualPayments';
import ClaimPayments from '../Payments';
import {
  BankTransactionsEnabledAtEligibilityState,
  getBankTransactionsEnabledAtDisabledReason,
} from './bank-transactions-enabled-at-eligibility';
import { getClaimDeletionButtonState, isClaimDeletionAllowed } from './claim-deletion-eligibility';
import {
  ClaimFinancialSummaryStatus,
  getCanonicalClaimOriginalPaymentPlanInterest,
  getCanonicalClaimPaymentOrder,
  getCanonicalClaimPaymentPlanInterest,
  getClaimFinancialSummaryState,
  getClaimFinancialSummaryStateWithBalance,
  getPrincipalAmountRemainingFromBalance,
  getTotalPaidAgainstFinalAmount,
  isAmountOwedUnfinalised,
  reportClaimFinancialSummaryInconsistency,
} from './claim-financial-summary';
import EditBankTransactionsEnabledAtModal from './EditBankTransactionsEnabledAtModal';

interface ClaimViewParams {
  id: string;
}

interface EditingClaimItemState {
  id: string;
  currentAmount: number;
  title: string;
  itemType: string;
}

interface CanonicalOrderBalanceState {
  orderId?: string;
  refreshKey?: string;
  amountRemaining?: number;
  includedPaymentPlanInterest?: number;
  isLoading: boolean;
}

const getClaimAddressLine = (claim: ClaimWithCheckoutRelationsDTO) => {
  const { address } = claim.checkout.tenancy.property;

  return [
    address.flatNumber ? `Flat ${address.flatNumber},` : null,
    address.buildingNumber,
    address.street,
    address.town,
    address.postcode,
  ]
    .filter(Boolean)
    .join(' ');
};

const NON_DESIGNABLE_PAYMENT_PLAN_STATUSES = [
  PaymentPlanDTOStatusEnum.COMPLETE,
  PaymentPlanDTOStatusEnum.CLOSED,
  PaymentPlanDTOStatusEnum.CANCELLED,
];

const UNPAID_PAYMENT_PLAN_INSTALMENT_STATUSES = [
  PaymentPlanInstalmentDTOStatusEnum.PENDING,
  PaymentPlanInstalmentDTOStatusEnum.FAILED,
  PaymentPlanInstalmentDTOStatusEnum.CANCELLED,
];

const ActionBar = styled.div<{ flexStart?: boolean }>`
  display: flex;
  padding: 24px 0 0;
  justify-content: flex-end;
  flex-wrap: wrap;

  ${(props) =>
    props.flexStart &&
    `
    justify-content: flex-start;
  `}

  button {
    margin-bottom: 10px;
    margin-right: 10px;
  }
`;

const ClaimStatus = styled.div`
  background: ${(props) => props.theme.colors.positive};
  border-radius: 24px;
  display: inline-block;
  color: #fff;
  font-family: ${(props) => props.theme.typography.face.secondary};
  font-size: 14px;
  font-weight: bold;
  margin: 0 0 0 auto;
  padding: 4px 20px;

  &.ACTIVE {
    background: ${(props) => props.theme.colors.positive};
  }

  &.REJECTED {
    background: ${(props) => props.theme.colors.negative};
  }

  &.AWAITING_RESPONSE {
    background: ${(props) => props.theme.colors.placeholder};
  }

  &.DEACTIVATED {
    background: ${(props) => props.theme.colors.negative};
  }

  &.ARBITRATED {
    background: ${(props) => props.theme.colors.warning};
  }
`;

const ClaimBadges = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-start;
  margin-top: 8px;

  ${ClaimStatus} {
    margin: 0;
  }
`;

const UploaderContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const UploaderDocumentName = styled.span`
  font-family: ${RepositTheme.typography.face.secondary};
  font-size: 12px;
  margin-left: 7px;
`;

const BrowseLabel = styled.label`
  background: #e0ecf7;
  border-color: #e0ecf7;
  height: 24px;
  width: 85px;
  border-radius: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 12px;
  color: ${(props) => props.theme.colors.primaryBrand};
  font-family: ${RepositTheme.typography.face.secondary};
  cursor: pointer;
`;

const InputFile = styled.input.attrs({
  type: 'file',
})`
  opacity: 0;
  position: absolute;
  z-index: -1;
`;

const BetaBadge = styled.div`
  background: ${(props) => props.theme.colors.warning};
  border-radius: 24px;
  display: inline-block;
  color: #fff;
  font-family: ${(props) => props.theme.typography.face.secondary};
  font-size: 14px;
  font-weight: bold;
  margin: 0 0 0 auto;
  padding: 4px 20px;
  margin-bottom: 6px;
`;

const DisabledButtonWrapper = styled.span`
  display: inline-block;
`;

const ARBITRATION_VALIDATION_SCHEMA = Yup.object().shape({
  arbitratedAmount: Yup.number()
    .typeError('Amount must be a number')
    .required('Required')
    .test('two-decimals', 'Amount must have a maximum of two decimal places', (value) => {
      return twoDPRegex.test(value);
    }),
});

const ClaimView: React.FC = () => {
  const dispatch = useAppDispatch();
  const { id } = useParams<ClaimViewParams>();
  useAdminDocumentTitle(id);
  const [standardModalState, setStandardModalState] = useState<ConfirmationModalProps>({} as ConfirmationModalProps);
  const [arbitrationFile, setArbitrationFile] = useState<File>();
  const [editingClaimItem, setEditingClaimItem] = useState<EditingClaimItemState | null>(null);
  const reportedClaimFinancialSummaryKey = useRef<string>();
  const loadingSetPaidAt = useSelector(createLoadingSelector([SET_PAID_AT_CLAIM_STORE_KEY]));
  const loadingCreateArbitration = useSelector(createLoadingSelector([CREATE_ARBITRATION_STORE_KEY]));
  const isDeletingClaim = useSelector(createLoadingSelector([DELETE_CLAIM_STORE_KEY]));
  const isPreviewingDeleteClaim = useSelector(createLoadingSelector([PREVIEW_DELETE_CLAIM_STORE_KEY]));
  const claimDeletionPreview = useSelector(getClaimDeletionPreview);
  const fetchClaim = useCallback(() => dispatch(fetchClaimRequested({ claimId: id })), [id, dispatch]);
  const isFetchClaimLoading = useSelector(createLoadingSelector([FETCH_CLAIM_STORE_KEY]));
  const hasClaimFetched = useSelector(getClaimHasBeenFetched);
  const claim = useSelector((app: AppState) => getFullClaimById(app, id));
  const tenancyOrderId: string | undefined = get(claim, 'checkout.tenancy.orders[0].id');
  const tenantsWithActions = useSelector((state: AppState) => {
    const tenancyOrderWithActions = tenancyOrderId ? getTenancyOrder(state, { tenancyOrderId }) : undefined;

    return tenancyOrderWithActions ? tenancyOrderWithActions.tenancy.tenants : undefined;
  });
  const repositNotes = useSelector((state: AppState) =>
    tenancyOrderId ? getTenancyOrderNotes(state, { tenancyOrderId }) : undefined,
  );
  const repositNotesError = useSelector(createErrorMessageSelector([FETCH_TENANCY_ORDER_NOTES_STORE_KEY]));
  const orderManualPayments = useSelector(getManualPaymentsByClaimId(id));
  const orderManualPaymentsTotal = orderManualPayments.reduce((acc, curr) => acc + curr.amount, 0);
  const claimFinancialSummaryBaseState = useMemo(() => getClaimFinancialSummaryState(claim), [claim]);
  const canonicalClaimPaymentOrder = getCanonicalClaimPaymentOrder(claimFinancialSummaryBaseState);
  const canonicalClaimPaymentOrderId = canonicalClaimPaymentOrder ? canonicalClaimPaymentOrder.id : undefined;
  const canonicalFinalAmount =
    claimFinancialSummaryBaseState.status === ClaimFinancialSummaryStatus.READY
      ? claimFinancialSummaryBaseState.finalAmount
      : undefined;
  const canonicalPaymentPlanInterestHint = getCanonicalClaimPaymentPlanInterest(claimFinancialSummaryBaseState);
  const canonicalOriginalPaymentPlanInterestAmount = getCanonicalClaimOriginalPaymentPlanInterest(claimFinancialSummaryBaseState);
  const canonicalBalanceOrderId =
    typeof canonicalFinalAmount === 'number' && canonicalFinalAmount > 0 ? canonicalClaimPaymentOrderId : undefined;
  const canonicalBalanceRefreshKey = canonicalBalanceOrderId
    ? [
        canonicalBalanceOrderId,
        canonicalFinalAmount,
        canonicalClaimPaymentOrder ? canonicalClaimPaymentOrder.amount : undefined,
        orderManualPaymentsTotal,
        typeof canonicalPaymentPlanInterestHint === 'number' ? canonicalPaymentPlanInterestHint : 'no-interest',
      ].join(':')
    : undefined;
  const [canonicalOrderBalance, setCanonicalOrderBalance] = useState<CanonicalOrderBalanceState>({ isLoading: false });
  const canonicalOrderBalanceMatchesRequest =
    canonicalOrderBalance.orderId === canonicalBalanceOrderId && canonicalOrderBalance.refreshKey === canonicalBalanceRefreshKey;
  const canonicalOrderAmountRemainingFromApi = canonicalOrderBalanceMatchesRequest
    ? canonicalOrderBalance.amountRemaining
    : undefined;
  const includedPaymentPlanInterestFromApi = canonicalOrderBalanceMatchesRequest
    ? canonicalOrderBalance.includedPaymentPlanInterest
    : undefined;
  const canonicalPaymentPlanInterestAmount =
    typeof includedPaymentPlanInterestFromApi === 'number'
      ? includedPaymentPlanInterestFromApi
      : canonicalOrderBalanceMatchesRequest
        ? canonicalPaymentPlanInterestHint
        : undefined;
  const canonicalOrderAmountRemaining = getPrincipalAmountRemainingFromBalance(
    canonicalOrderAmountRemainingFromApi,
    canonicalPaymentPlanInterestAmount,
  );
  const claimFinancialSummaryState = getClaimFinancialSummaryStateWithBalance(
    claimFinancialSummaryBaseState,
    canonicalOrderAmountRemaining,
  );
  const claimFinancialSummaryReportKey =
    claimFinancialSummaryState.status === ClaimFinancialSummaryStatus.INCONSISTENT
      ? `${claimFinancialSummaryState.claimId}:${claimFinancialSummaryState.reason}`
      : undefined;
  const hasIncludedPaymentPlanInterest =
    typeof canonicalPaymentPlanInterestAmount === 'number' && canonicalPaymentPlanInterestAmount > 0;
  const canonicalOrderAmountRemainingIncludingInterest = hasIncludedPaymentPlanInterest
    ? canonicalOrderAmountRemainingFromApi
    : undefined;
  const totalPaidAgainstFinalAmount = getTotalPaidAgainstFinalAmount(claimFinancialSummaryState, canonicalOrderAmountRemaining);
  const isTotalPaidLoading = !!(
    canonicalBalanceOrderId &&
    (canonicalOrderBalance.orderId !== canonicalBalanceOrderId ||
      canonicalOrderBalance.refreshKey !== canonicalBalanceRefreshKey ||
      canonicalOrderBalance.isLoading)
  );
  const isPaymentPlanBalanceLoading = typeof canonicalPaymentPlanInterestHint === 'number' && isTotalPaidLoading;
  const claimStatus = claim ? claim.status : undefined;
  const isClaimResolved = !!claim && claim.status === ClaimWithCheckoutRelationsDTOStatusEnum.RESOLVED;
  const hasClaimUpdateAutoChargeRole = useSelector((state: AppState) =>
    hasInternalRole(state, UserDTOInternalRolesEnum.CLAIMUPDATEAUTOCHARGE),
  );
  const hasClaimItemCorrectiveChangeRole = useSelector((state: AppState) =>
    hasInternalRole(state, UserDTOInternalRolesEnum.CLAIMITEMCORRECTIVECHANGE),
  );
  const hasClaimDeleteRole = useSelector((state: AppState) => hasInternalRole(state, UserDTOInternalRolesEnum.CLAIMDELETE));
  const hasClaimUpdateBankTransactionsEnabledAtRole = useSelector((state: AppState) =>
    hasInternalRole(state, UserDTOInternalRolesEnum.CLAIMUPDATEBANKTRANSACTIONSENABLEDAT),
  );
  // The earliest bank transfer import date can only be set once, on a claim that has never had one.
  const canSetBankTransactionsEnabledAt =
    hasClaimUpdateBankTransactionsEnabledAtRole && !!claim && !claim.bankTransactionsEnabledAt;
  const [bankTransactionsEnabledAtEligibility, setBankTransactionsEnabledAtEligibility] =
    useState<BankTransactionsEnabledAtEligibilityState>({ isLoading: true });
  const hasClaimExtendResponseDeadlineRole = useSelector((state: AppState) =>
    hasInternalRole(state, UserDTOInternalRolesEnum.CLAIMEXTENDRESPONSEDEADLINE),
  );
  const hasEvidenceWorkspaceRole = useSelector((state: AppState) =>
    hasInternalRole(state, UserDTOInternalRolesEnum.EVIDENCEWORKSPACE),
  );
  // this is looking for a pending payment plan order specifically
  const pendingOrderForPaymentPlan = useSelector(getPendingPaymentPlanOrder(id));
  const pendingOrderForPaymentPlanId = pendingOrderForPaymentPlan ? pendingOrderForPaymentPlan.id : undefined;
  const ppOrderCustomers = get(pendingOrderForPaymentPlan, 'orderCustomers', []);
  const pendingOrderHasPaymentPlan = ppOrderCustomers.some((orderCustomer) => !!orderCustomer.paymentPlan);
  const isClaimProposalPaymentPlanBlocked = !!(
    pendingOrderForPaymentPlan && pendingOrderForPaymentPlan.productId === OrderWithOrderCustomersDTOProductIdEnum.CLAIMPROPOSAL
  );
  const isPaymentPlanBlockedUntilAmountFinalised = isAmountOwedUnfinalised(claimFinancialSummaryBaseState);
  const paymentPlan = useSelector(getPaymentPlanByClaimId(id));

  const paymentPlanCustomer = useSelector(getPaymentPlanCustomerByClaimId(id));
  const ppOrderAmountRemaining = useSelector(getOrderAmountRemaining);

  const tenancyInventory = useSelector(getTenancyInventoryByClaimId(id));

  const isLegacyClaim = !(claim && claim.mediationEnabled);
  const autoChargeEnabled = get(claim, 'autoChargeEnabled', true);
  const currentClaimModal = useSelector((state: AppState) => state.claim.currentClaimActionModal);
  const { renderConfirmationModal: renderStandardConfirmationModal, showModal: showStandardModal } =
    useConfirmationModal(standardModalState);
  const [globalFlashMessagePayload, onDismissGlobalFlashMessage] = useFlashMessage([
    REOPEN_CLAIM_STORE_KEY,
    DOWNLOAD_DOCUMENTS_STORE_KEY,
    CREATE_ARBITRATION_STORE_KEY,
    WAIVE_CLAIM_DISPUTE_FEE_STORE_KEY,
    GENERATE_ADR_FORM_STORE_KEY,
    OPEN_EVIDENCE_WORKSPACE_STORE_KEY,
    INTERNAL_UPDATE_CLAIM_ITEM_STORE_KEY,
    PREVIEW_CLAIM_ITEM_UPDATE_STORE_KEY,
    DELETE_PAYMENT_PLAN_INSTALMENT_STORE_KEY,
    PAYMENT_PLAN_INSTALMENT_DESIGNER_STORE_KEY,
    RESCHEDULE_PAYMENT_PLAN_INSTALMENT_STORE_KEY,
    UPDATE_PAYMENT_PLAN_TENANT_DEFERRAL_STORE_KEY,
  ]);

  const pendingClaimItemPreview = useSelector(getPendingClaimItemPreview);

  const [createArbitrationFlashMessagePayload, onDismissCreateArbitrationFlashMessage] = useFlashMessage([
    CREATE_ARBITRATION_STORE_KEY,
  ]);

  const mediationClaimItems = useSelector((state: AppState) => getMediationClaimItemsByClaimId(state, get(claim, 'id', '')));

  const deletedItems = claim && claim.items.filter((item) => !!item.deletedAt);
  const activeItems = claim && claim.items.filter((item) => !item.deletedAt);
  const activeMediationItems = mediationClaimItems.filter((i) => !i.deletedAt);

  const mediationEnabled = !!(claim && claim.mediationEnabled);
  const hasInsuranceBeenSet = !!(claim && claim.insuranceDatesSetAt);

  const paymentPlanIsComplete = paymentPlan && paymentPlan.status === PaymentPlanDTOStatusEnum.COMPLETE;

  const pendingEvidenceChaser =
    claim &&
    claim.evidenceChasers &&
    claim.evidenceChasers.find((chaser) => chaser.status === EvidenceChaserDTOStatusEnum.PENDING);

  const isCloseButtonVisible = useCallback(() => {
    if (
      (!paymentPlan || paymentPlanIsComplete) &&
      (claim.status === ClaimWithCheckoutRelationsDTOStatusEnum.DRAFT ||
        claim.status === ClaimWithCheckoutRelationsDTOStatusEnum.AWAITINGREVIEW ||
        claim.status === ClaimWithCheckoutRelationsDTOStatusEnum.AWAITINGADDITIONALINFORMATION ||
        claim.status === ClaimWithCheckoutRelationsDTOStatusEnum.AWAITINGRESPONDENTS)
    ) {
      return true;
    }

    return false;
  }, [claim, paymentPlan, paymentPlanIsComplete]);

  const isResolveButtonVisible = useCallback(() => {
    if (
      (!paymentPlan || paymentPlanIsComplete) &&
      claim.status !== ClaimWithCheckoutRelationsDTOStatusEnum.RESOLVED &&
      claim.status !== ClaimWithCheckoutRelationsDTOStatusEnum.DEACTIVATED
    ) {
      return true;
    }
    return false;
  }, [claim, paymentPlan, paymentPlanIsComplete]);

  const isADRFormButtonVisible = useCallback(() => {
    const isDisputed = claim.proposals && claim.proposals.some((p) => p.status === ClaimProposalDTOStatusEnum.DISPUTED);
    if (
      (claim.status === ClaimWithCheckoutRelationsDTOStatusEnum.AWAITINGARBITRATION ||
        (claim.status === ClaimWithCheckoutRelationsDTOStatusEnum.AWAITINGEVIDENCEREVIEW && isDisputed)) &&
      !claim.arbitration
    ) {
      return true;
    }
    return false;
  }, [claim]);

  const isCreateArbitrationButtonVisible = useCallback(() => {
    if (
      (claim.status === ClaimWithCheckoutRelationsDTOStatusEnum.ARBITRATION ||
        claim.status === ClaimWithCheckoutRelationsDTOStatusEnum.AWAITINGARBITRATION) &&
      !claim.arbitration
    ) {
      return true;
    }
    return false;
  }, [claim]);

  const isWaiveDisputeFeeButtonVisible = useCallback(() => {
    // Should this just appear all the time?
    if (claim.status === ClaimWithCheckoutRelationsDTOStatusEnum.AWAITINGARBITRATIONADMINFEE) {
      return true;
    }
    return false;
  }, [claim]);

  const isReopenButtonVisible = useCallback(() => {
    if (claim.status === ClaimWithCheckoutRelationsDTOStatusEnum.DEACTIVATED) {
      return true;
    }
    return false;
  }, [claim]);

  // Statuses where a response is still awaited, plus AWAITING_SUBSTANTIATION, which follows a tenant auto-response that
  // tenancy can reverse.
  const isExtendResponseDeadlineButtonVisible = useCallback(() => {
    const extendableStatuses: string[] = [
      ClaimWithCheckoutRelationsDTOStatusEnum.AWAITINGRESPONDENTS,
      ClaimWithCheckoutRelationsDTOStatusEnum.AWAITINGARBITRATIONADMINFEE,
      ClaimWithCheckoutRelationsDTOStatusEnum.MEDIATION,
      ClaimWithCheckoutRelationsDTOStatusEnum.AWAITINGSUBSTANTIATION,
    ];
    return hasClaimExtendResponseDeadlineRole && extendableStatuses.includes(claim.status);
  }, [claim, hasClaimExtendResponseDeadlineRole]);

  const isDeleteButtonVisible = useCallback(() => {
    return isClaimDeletionAllowed(hasClaimDeleteRole, claim.status);
  }, [claim, hasClaimDeleteRole]);
  const deleteClaimButtonState = getClaimDeletionButtonState(claimDeletionPreview, isPreviewingDeleteClaim);

  const isEvidenceReviewButtonVisible = useCallback(() => {
    if (
      !claim.evidenceChaserSkippedAt &&
      (claim.status === ClaimWithCheckoutRelationsDTOStatusEnum.AWAITINGEVIDENCEREVIEW ||
        claim.status === ClaimWithCheckoutRelationsDTOStatusEnum.EVIDENCEREVIEW ||
        (claim.evidenceChasers && claim.evidenceChasers.length) ||
        claim.status === ClaimWithCheckoutRelationsDTOStatusEnum.ARBITRATED)
    ) {
      return true;
    }
    return false;
  }, [claim]);

  const shouldShowViewExistingEvidenceReviewWhenArbitratedButton = useCallback(() => {
    const isClaimArbitrated = claim.status === ClaimWithCheckoutRelationsDTOStatusEnum.ARBITRATED;
    const hasPreADRChaser = !!(
      claim.evidenceChasers && claim.evidenceChasers.find((chaser) => chaser.type === EvidenceChaserDTOTypeEnum.PREADR)
    );
    const hasPostADRChaser = !!(
      claim.evidenceChasers && claim.evidenceChasers.find((chaser) => chaser.type === EvidenceChaserDTOTypeEnum.POSTADR)
    );

    if (isClaimArbitrated && hasPreADRChaser && !hasPostADRChaser && !hasInsuranceBeenSet) {
      return true;
    }

    return false;
  }, [claim, hasInsuranceBeenSet]);

  const shouldCreateNewEvidenceChaser = useCallback(() => {
    // this assumes that the button is visible

    // no evidence chasers at all -> true
    const noEvidenceChasers = !claim.evidenceChasers || claim.evidenceChasers.length === 0;
    if (noEvidenceChasers) {
      return true;
    }

    // if arbitrated and no post ADR chaser -> true
    const isArbitrated = claim.status === ClaimWithCheckoutRelationsDTOStatusEnum.ARBITRATED;
    const hasPostADRChaser = !!(
      claim.evidenceChasers && claim.evidenceChasers.find((chaser) => chaser.type === EvidenceChaserDTOTypeEnum.POSTADR)
    );
    if (isArbitrated && !hasPostADRChaser && !hasInsuranceBeenSet) {
      return true;
    }
    return false;
  }, [claim, hasInsuranceBeenSet]);

  const showShowSkipEvidenceChasersButton = () => {
    const isAwaitingEvidenceReview = claim.status === ClaimWithCheckoutRelationsDTOStatusEnum.AWAITINGEVIDENCEREVIEW;
    const hasNotSkippedEvidenceChasers = !claim.evidenceChaserSkippedAt;
    const hasPendingEvidenceChaser =
      claim.evidenceChasers && claim.evidenceChasers.some((chaser) => chaser.status === EvidenceChaserDTOStatusEnum.PENDING);
    return isAwaitingEvidenceReview && hasNotSkippedEvidenceChasers && !hasPendingEvidenceChaser;
  };

  const handleResolveClaim = useCallback(
    (reason?: string, notification?: NotificationInfo) => {
      if (id && reason && notification) {
        dispatch(
          resolveClaimApiRequested({
            claimId: id,
            reason,
            sendTenantNotifications: notification.sendTenantNotification,
            sendSupplierNotifications: notification.sendSupplierNotification,
          }),
        );
      }
    },
    [id, dispatch],
  );

  const handleCloseClaim = useCallback(
    (reason?: string, notification?: NotificationInfo) => {
      if (id && reason && notification) {
        dispatch(
          deactivateClaimApiRequested({
            claimId: id,
            reason,
            sendTenantNotifications: notification.sendTenantNotification,
            sendSupplierNotifications: notification.sendSupplierNotification,
          }),
        );
      }
    },
    [id, dispatch],
  );

  const handleSetPaid = (values: { paidAt?: Date }) => {
    if (values.paidAt) {
      dispatch(setPaidAtApiRequested({ claimId: id, paidAt: values.paidAt.toString() }));
    }
  };

  const handleCreateArbitration = (values: { arbitratedAmount?: number }) => {
    if (values.arbitratedAmount || values.arbitratedAmount === 0) {
      dispatch(
        createArbitrationApiRequested({
          claimId: id,
          arbitratedAmount: Math.round(Number(values.arbitratedAmount) * 100),
          file: arbitrationFile,
        }),
      );
    }
  };

  const handleOpenEditModal = useCallback(
    (claimItemId: string, currentAmount: number, title: string, itemType: string) => {
      if (isClaimResolved || !hasClaimItemCorrectiveChangeRole) return;
      setEditingClaimItem({ id: claimItemId, currentAmount, title, itemType });
    },
    [hasClaimItemCorrectiveChangeRole, isClaimResolved],
  );

  const handleDismissClaimItemModal = useCallback(() => {
    setEditingClaimItem(null);
    dispatch(clearClaimItemPreview());
  }, [dispatch]);

  const handleUpdateClaimItemAmount = useCallback(
    (claimItemId: string, newAmount: number) => {
      if (!hasClaimItemCorrectiveChangeRole) return;
      dispatch(
        previewClaimItemUpdateRequested({
          claimId: id,
          claimItemId,
          data: { newAmount },
        }),
      );
    },
    [dispatch, hasClaimItemCorrectiveChangeRole, id],
  );

  const handleConfirmClaimItemUpdate = useCallback(() => {
    if (!pendingClaimItemPreview || !hasClaimItemCorrectiveChangeRole) return;
    setEditingClaimItem(null);
    dispatch(internalUpdateClaimItemRequested(pendingClaimItemPreview.payload));
  }, [dispatch, hasClaimItemCorrectiveChangeRole, pendingClaimItemPreview]);

  const toggleUpdateAutoCharge = useCallback(() => {
    if (!hasClaimUpdateAutoChargeRole) return;
    dispatch(updateAutoChargeApiRequested({ claimId: id, autoChargeEnabled: !autoChargeEnabled }));
  }, [autoChargeEnabled, hasClaimUpdateAutoChargeRole, id, dispatch]);

  // leaving here
  // const downloadDocumentsLocal = useCallback(() => {
  //   appDispatch(setCurrentClaimActionModal(''));
  //   if (!isBeingDownloaded) {
  //     appDispatch(startCurrentClaimZipDownload(id));
  //     return appDispatch(downloadDocumentsLocally(id));
  //   }
  // }, [id, appDispatch, isBeingDownloaded]);

  useEffect(() => {
    fetchClaim();
  }, [fetchClaim]);

  useEffect(() => {
    if (tenancyOrderId) {
      dispatch(fetchTenancyOrderRequested(tenancyOrderId));
      dispatch(fetchTenancyOrderNotesRequested(tenancyOrderId));
    }
  }, [dispatch, tenancyOrderId]);

  useEffect(() => {
    dispatch(clearDeleteClaimPreview());

    if (claimStatus && isClaimDeletionAllowed(hasClaimDeleteRole, claimStatus)) {
      dispatch(previewDeleteClaimRequested({ claimId: id }));
    }

    return function cleanUp() {
      dispatch(clearDeleteClaimPreview());
    };
  }, [claimStatus, dispatch, hasClaimDeleteRole, id]);

  useEffect(() => {
    if (pendingOrderForPaymentPlanId) {
      dispatch(
        fetchAmountRemainingForPaymentPlanOrderThunk({
          orderId: pendingOrderForPaymentPlanId,
          includeInterest: pendingOrderHasPaymentPlan,
        }),
      );
    }

    return function cleanUp() {
      dispatch(clearAmountRemaining());
    };
  }, [pendingOrderForPaymentPlanId, pendingOrderHasPaymentPlan, dispatch]);

  useEffect(() => {
    let isCurrentRequest = true;

    if (!canonicalBalanceOrderId || !canonicalBalanceRefreshKey) {
      setCanonicalOrderBalance({ isLoading: false });
      return;
    }

    setCanonicalOrderBalance({
      orderId: canonicalBalanceOrderId,
      refreshKey: canonicalBalanceRefreshKey,
      isLoading: true,
    });
    dispatch(fetchAmountRemainingDetailsForOrderThunk({ orderId: canonicalBalanceOrderId })).then((action) => {
      if (!isCurrentRequest) return;

      const balanceDetails = fetchAmountRemainingDetailsForOrderThunk.fulfilled.match(action) ? action.payload : undefined;

      setCanonicalOrderBalance({
        orderId: canonicalBalanceOrderId,
        refreshKey: canonicalBalanceRefreshKey,
        amountRemaining: balanceDetails ? balanceDetails.amountRemaining : undefined,
        includedPaymentPlanInterest: balanceDetails ? balanceDetails.includedPaymentPlanInterest : undefined,
        isLoading: false,
      });
    });

    return () => {
      isCurrentRequest = false;
    };
  }, [canonicalBalanceOrderId, canonicalBalanceRefreshKey, dispatch]);

  useEffect(() => {
    let isCurrentRequest = true;

    if (!canSetBankTransactionsEnabledAt) {
      return;
    }

    setBankTransactionsEnabledAtEligibility({ isLoading: true });
    dispatch(fetchBankTransactionsEnabledAtEligibilityThunk({ claimId: id })).then((action) => {
      if (!isCurrentRequest) return;

      setBankTransactionsEnabledAtEligibility({
        isLoading: false,
        eligibility: fetchBankTransactionsEnabledAtEligibilityThunk.fulfilled.match(action) ? action.payload : undefined,
      });
    });

    return () => {
      isCurrentRequest = false;
    };
  }, [canSetBankTransactionsEnabledAt, id, dispatch]);

  useEffect(() => {
    if (!claimFinancialSummaryReportKey) {
      reportedClaimFinancialSummaryKey.current = undefined;
      return;
    }
    if (reportedClaimFinancialSummaryKey.current === claimFinancialSummaryReportKey) return;

    reportedClaimFinancialSummaryKey.current = claimFinancialSummaryReportKey;
    reportClaimFinancialSummaryInconsistency(claimFinancialSummaryState);
  }, [claimFinancialSummaryReportKey, claimFinancialSummaryState]);

  useEffect(() => {
    if (!currentClaimModal) {
      return showStandardModal(false);
    }

    if (currentClaimModal === 'resolve') {
      setStandardModalState({
        onSubmit: (reason?: string, notification?: NotificationInfo) => handleResolveClaim(reason, notification),
        text: 'Are you sure you would like to resolve this Claim?',
        title: 'Resolve Claim!',
        enableDetailsField: true,
        enableNotificationsFields: true,
        loadingActions: [RESOLVE_CLAIM_STORE_KEY],
        onDismiss: () => dispatch(setCurrentClaimActionModal('')),
      });

      showStandardModal(true);
    } else if (currentClaimModal === 'close') {
      setStandardModalState({
        onSubmit: (reason?: string, notification?: NotificationInfo) => handleCloseClaim(reason, notification),
        text: 'Are you sure you would like to close this Claim?',
        title: 'Close Claim!',
        enableDetailsField: true,
        enableNotificationsFields: true,
        loadingActions: [DEACTIVATE_CLAIM_STORE_KEY],
        onDismiss: () => dispatch(setCurrentClaimActionModal('')),
      });

      showStandardModal(true);
    } else if (currentClaimModal === 'autoCharge') {
      setStandardModalState({
        onSubmit: toggleUpdateAutoCharge,
        text: 'Are you sure you would like to modify the auto charge status?',
        title: autoChargeEnabled ? 'Disable Auto Charge' : 'Enable Auto Charge',
        loadingActions: [UPDATE_AUTO_CHARGE_STORE_KEY],
        onDismiss: () => dispatch(setCurrentClaimActionModal('')),
      });

      showStandardModal(true);
    } else if (currentClaimModal === 'createEvidenceReview') {
      showStandardModal(false);
    } else if (currentClaimModal === 'downloadDocuments') {
      showStandardModal(false);
    } else if (currentClaimModal === 'createPaymentPlan') {
      showStandardModal(false);
    } else if (currentClaimModal === 'deletePaymentPlan') {
      showStandardModal(false);
    } else if (currentClaimModal === 'forceDeletePaymentPlan') {
      showStandardModal(false);
    } else if (currentClaimModal === 'skipEvidenceChasers') {
      setStandardModalState({
        onSubmit: () => dispatch(skipEvidenceChasersApiRequested({ claimId: id })),
        text: 'Are you sure you would like to mark all evidence as provided? This action cannot be undone.',
        title: 'Mark All Evidence as Provided',
        loadingActions: [SKIP_EVIDENCE_CHASERS_STORE_KEY],
        onDismiss: () => dispatch(setCurrentClaimActionModal('')),
      });

      showStandardModal(true);
    } else if (currentClaimModal === 'expireEvidenceChaser' && pendingEvidenceChaser) {
      setStandardModalState({
        onSubmit: () => dispatch(expireEvidenceChaserApiRequested({ claimId: id, evidenceChaserId: pendingEvidenceChaser.id })),
        title: 'Expire Evidence Request',
        text: 'Are you sure you would like to expire this evidence request? This action cannot be undone.',
        loadingActions: [EXPIRE_EVIDENCE_CHASER_STORE_KEY],
        onDismiss: () => dispatch(setCurrentClaimActionModal('')),
      });

      showStandardModal(true);
    }
  }, [
    currentClaimModal,
    showStandardModal,
    handleResolveClaim,
    handleCloseClaim,
    dispatch,
    toggleUpdateAutoCharge,
    autoChargeEnabled,
    id,
    pendingEvidenceChaser,
  ]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      setArbitrationFile(event.target.files[0]);
    }
  };

  const isSubmittingReopenSelector = createLoadingSelector([REOPEN_CLAIM_STORE_KEY]);
  const isSubmittingReopen = useSelector(isSubmittingReopenSelector);

  const isAgentDeciding = useSelector((state: AppState) => getIsAgentDeciding(state, get(claim, 'id', '')));
  const isTenantDeciding = useSelector((state: AppState) => getIsTenantDeciding(state, get(claim, 'id', '')));

  const isDownloadingClaimDocumentsSelector = createLoadingSelector([DOWNLOAD_DOCUMENTS_STORE_KEY]);
  const isDownloadingClaimDocuments = useSelector(isDownloadingClaimDocumentsSelector);

  const isCreatingEvidenceChasersSelector = createLoadingSelector([CREATE_EVIDENCE_CHASERS_STORE_KEY]);
  const isCreatingEvidenceChasers = useSelector(isCreatingEvidenceChasersSelector);
  const isUpdatingClaimItemAmount = useSelector(createLoadingSelector([INTERNAL_UPDATE_CLAIM_ITEM_STORE_KEY]));
  const isPreviewingClaimItemUpdate = useSelector(createLoadingSelector([PREVIEW_CLAIM_ITEM_UPDATE_STORE_KEY]));

  const hasCreatePaymentPlanRole = useSelector((state: AppState) =>
    hasInternalRole(state, UserDTOInternalRolesEnum.PAYMENTPLANCREATE),
  );

  const hasDeletePaymentPlanRole = useSelector((state: AppState) =>
    hasInternalRole(state, UserDTOInternalRolesEnum.PAYMENTPLANDELETE),
  );

  const hasDeletePaymentPlanInstalmentRole = useSelector((state: AppState) =>
    hasInternalRole(state, UserDTOInternalRolesEnum.PAYMENTPLANINSTALMENTDELETE),
  );

  const hasForceDeletePaymentPlanRole = useSelector((state: AppState) =>
    hasInternalRole(state, UserDTOInternalRolesEnum.PAYMENTPLANDELETEFORCE),
  );

  const hasPaymentPlanInstalmentDesignerRole = useSelector((state: AppState) =>
    hasInternalRole(state, UserDTOInternalRolesEnum.PAYMENTPLANINSTALMENTDESIGNER),
  );

  const hasStandardDeletablePaymentPlanStatus =
    paymentPlan &&
    (paymentPlan.status === PaymentPlanDTOStatusEnum.CANCELLED ||
      paymentPlan.status === PaymentPlanDTOStatusEnum.PENDING ||
      paymentPlan.status === PaymentPlanDTOStatusEnum.DEFAULT);
  const hasForceDeletablePaymentPlanStatus = paymentPlan && paymentPlan.status === PaymentPlanDTOStatusEnum.ACTIVE;

  const [isPPSubmitting, setIsPPSubmitting] = useState(false);
  const [isDeletePPSubmitting, setIsDeletePPSubmitting] = useState(false);
  const [deletingPaymentPlanInstalmentId, setDeletingPaymentPlanInstalmentId] = useState<string | undefined>();
  const [togglingTenantDeferral, setTogglingTenantDeferral] = useState(false);
  const [previewingPaymentPlanInstalmentRescheduleId, setPreviewingPaymentPlanInstalmentRescheduleId] = useState<
    string | undefined
  >();
  const [reschedulingPaymentPlanInstalmentId, setReschedulingPaymentPlanInstalmentId] = useState<string | undefined>();
  const [isPaymentPlanInstalmentDesignerOpen, setIsPaymentPlanInstalmentDesignerOpen] = useState(false);
  const [paymentPlanInstalmentDesignerPreview, setPaymentPlanInstalmentDesignerPreview] = useState<
    PaymentPlanInstalmentDesignerPreview | undefined
  >();
  const [paymentPlanInstalmentDesignerFile, setPaymentPlanInstalmentDesignerFile] = useState<File | undefined>();
  const [isPaymentPlanInstalmentDesignerPreviewing, setIsPaymentPlanInstalmentDesignerPreviewing] = useState(false);
  const [isPaymentPlanInstalmentDesignerConfirming, setIsPaymentPlanInstalmentDesignerConfirming] = useState(false);
  const [paymentPlanInstalmentDesignerPreviewError, setPaymentPlanInstalmentDesignerPreviewError] = useState<
    string | undefined
  >();
  const paymentPlanInstalmentDesignerPreviewRequestId = useRef(0);
  const paymentPlanInstalmentReschedulePreviewRequestId = useRef(0);
  const reschedulingPaymentPlanInstalmentIdRef = useRef<string | undefined>();
  const paymentPlanScheduleKeyForInstalmentDesigner = paymentPlan
    ? [
        paymentPlan.id,
        paymentPlan.status,
        paymentPlan.amount,
        ...(paymentPlan.instalments || [])
          .filter((instalment) => instalment.status === PaymentPlanInstalmentDTOStatusEnum.PENDING)
          .sort((a, b) => a.instalmentNumber - b.instalmentNumber)
          .map((instalment) => `${instalment.id}:${instalment.instalmentNumber}:${instalment.dueDate}:${instalment.amount}`),
      ].join('|')
    : undefined;
  const previousPaymentPlanScheduleKeyForInstalmentDesigner = useRef(paymentPlanScheduleKeyForInstalmentDesigner);
  const [isCreatingADRForm, setIsCreatingADRForm] = useState(false);

  const [isFeeBeingWaived, setIsFeeBeingWaived] = useState(false);

  useEffect(() => {
    // The preview belongs to one payment plan schedule. Clear it if a refetch swaps, removes, or changes the plan.
    if (previousPaymentPlanScheduleKeyForInstalmentDesigner.current !== paymentPlanScheduleKeyForInstalmentDesigner) {
      paymentPlanInstalmentDesignerPreviewRequestId.current += 1;
      setIsPaymentPlanInstalmentDesignerOpen(false);
      setPaymentPlanInstalmentDesignerFile(undefined);
      setIsPaymentPlanInstalmentDesignerPreviewing(false);
      setIsPaymentPlanInstalmentDesignerConfirming(false);
      setPaymentPlanInstalmentDesignerPreview(undefined);
      setPaymentPlanInstalmentDesignerPreviewError(undefined);
    }

    previousPaymentPlanScheduleKeyForInstalmentDesigner.current = paymentPlanScheduleKeyForInstalmentDesigner;
  }, [paymentPlanScheduleKeyForInstalmentDesigner]);

  const renderClaimDetails = (claim: ClaimWithCheckoutRelationsDTO, isAgentDeciding: boolean, isTenantDeciding: boolean) => {
    const { checkout, repositMessage, notes, details, documents, agentResponse, status, proposals } = claim;
    const claimAddressLine = getClaimAddressLine(claim);

    const { tenancy } = checkout;
    const { orders, property, tenants } = tenancy;
    const { landlord, address } = property;
    const tenancyOrder = orders && orders.length ? orders[0] : undefined;
    const autoChargeButtonMessage = `${autoChargeEnabled ? 'Disable' : 'Enable'} Auto Charge`;
    const autoChargeDisabledReason = hasClaimUpdateAutoChargeRole
      ? undefined
      : 'You do not have permission to update auto charge.';
    const claimItemEditDisabledReason = isClaimResolved
      ? 'Claim items cannot be modified once a claim is resolved.'
      : !hasClaimItemCorrectiveChangeRole
        ? 'You do not have permission to make corrective changes to claim items.'
        : undefined;
    // when merging the statuses in, it can create duplicates
    // easier to fix here
    const uniqueStatuses = uniqBy(claim.statuses, 'id');

    const createPaymentPlan = async (val: PaymentPlanFormValues) => {
      setIsPPSubmitting(true);
      return dispatch(
        createPaymentPlanThunk({
          orderCustomerId: val.orderCustomerId,
          productType: val.productType,
          // to pence
          amountPerInstalment: Math.round(val.amount * 100),
          // this needs to be optional in the previous component for some reason
          startDate: val.startDate as Date,
          claimId: id,
          manualPaymentsTotal: val.manualPaymentsTotal ? Math.round(val.manualPaymentsTotal * 100) : undefined,
          isHistoric: val.isHistoric === HISTORIC_PAYMENT_PLAN.YES,
          interestAmount: val.interestAmount ? Math.round(val.interestAmount * 100) : undefined,
        }),
      )
        .then(unwrapResult)
        .then(() => {
          dispatch(setCurrentClaimActionModal(''));
        })
        .finally(() => setIsPPSubmitting(false));
    };

    const deletePaymentPlan = async () => {
      setIsDeletePPSubmitting(true);
      // have to do this because of the types
      const paymentPlanId = paymentPlan ? paymentPlan.id : '';
      return dispatch(deletePaymentPlanThunk({ claimId: claim.id, paymentPlanId }))
        .then(unwrapResult)
        .then(() => {
          dispatch(setCurrentClaimActionModal(''));
        })
        .finally(() => setIsDeletePPSubmitting(false));
    };

    const forceDeletePaymentPlan = async () => {
      setIsDeletePPSubmitting(true);
      // have to do this because of the types
      const paymentPlanId = paymentPlan ? paymentPlan.id : '';
      return dispatch(forceDeletePaymentPlanThunk({ claimId: claim.id, paymentPlanId }))
        .then(unwrapResult)
        .then(() => {
          dispatch(setCurrentClaimActionModal(''));
        })
        .finally(() => setIsDeletePPSubmitting(false));
    };

    const deletePaymentPlanInstalment = async (instalmentId: string) => {
      if (!paymentPlan) return false;
      setDeletingPaymentPlanInstalmentId(instalmentId);
      return dispatch(deletePaymentPlanInstalmentThunk({ claimId: claim.id, paymentPlanId: paymentPlan.id, instalmentId }))
        .then(unwrapResult)
        .then(() => true)
        .catch(() => false)
        .finally(() => setDeletingPaymentPlanInstalmentId(undefined));
    };

    const toggleTenantDeferral = async (enabled: boolean) => {
      if (!paymentPlan) return false;
      setTogglingTenantDeferral(true);
      return dispatch(
        updatePaymentPlanTenantDeferralThunk({
          claimId: claim.id,
          paymentPlanId: paymentPlan.id,
          enabled,
        }),
      )
        .then(unwrapResult)
        .then(() => true)
        .catch(() => false)
        .finally(() => setTogglingTenantDeferral(false));
    };

    const previewPaymentPlanInstalmentReschedule = async (
      instalmentId: string,
      rescheduledDate: string,
    ): Promise<PaymentPlanInstalmentReschedulePreviewDTO | undefined> => {
      if (!paymentPlan) return undefined;
      const previewRequestId = paymentPlanInstalmentReschedulePreviewRequestId.current + 1;
      paymentPlanInstalmentReschedulePreviewRequestId.current = previewRequestId;
      setPreviewingPaymentPlanInstalmentRescheduleId(instalmentId);
      return dispatch(
        previewPaymentPlanInstalmentRescheduleThunk({
          claimId: claim.id,
          paymentPlanId: paymentPlan.id,
          instalmentId,
          rescheduledDate,
        }),
      )
        .then(unwrapResult)
        .then((preview) => (paymentPlanInstalmentReschedulePreviewRequestId.current === previewRequestId ? preview : undefined))
        .catch(() => undefined)
        .finally(() => {
          if (paymentPlanInstalmentReschedulePreviewRequestId.current === previewRequestId) {
            setPreviewingPaymentPlanInstalmentRescheduleId(undefined);
          }
        });
    };

    const reschedulePaymentPlanInstalment = async (
      instalmentId: string,
      rescheduledDate: string,
      expectedCurrentScheduleHash: string,
    ) => {
      if (!paymentPlan || reschedulingPaymentPlanInstalmentIdRef.current) return false;
      reschedulingPaymentPlanInstalmentIdRef.current = instalmentId;
      setReschedulingPaymentPlanInstalmentId(instalmentId);
      return dispatch(
        reschedulePaymentPlanInstalmentThunk({
          claimId: claim.id,
          paymentPlanId: paymentPlan.id,
          instalmentId,
          rescheduledDate,
          expectedCurrentScheduleHash,
        }),
      )
        .then(unwrapResult)
        .then(() => true)
        .catch(() => false)
        .finally(() => {
          if (reschedulingPaymentPlanInstalmentIdRef.current === instalmentId) {
            reschedulingPaymentPlanInstalmentIdRef.current = undefined;
            setReschedulingPaymentPlanInstalmentId(undefined);
          }
        });
    };

    const closePaymentPlanInstalmentDesigner = () => {
      paymentPlanInstalmentDesignerPreviewRequestId.current += 1;
      setIsPaymentPlanInstalmentDesignerOpen(false);
      setPaymentPlanInstalmentDesignerFile(undefined);
      setIsPaymentPlanInstalmentDesignerPreviewing(false);
      setIsPaymentPlanInstalmentDesignerConfirming(false);
      setPaymentPlanInstalmentDesignerPreview(undefined);
      setPaymentPlanInstalmentDesignerPreviewError(undefined);
    };

    const resetPaymentPlanInstalmentDesignerPreview = () => {
      paymentPlanInstalmentDesignerPreviewRequestId.current += 1;
      setIsPaymentPlanInstalmentDesignerPreviewing(false);
      setPaymentPlanInstalmentDesignerPreview(undefined);
      setPaymentPlanInstalmentDesignerPreviewError(undefined);
    };

    const previewPaymentPlanInstalmentDesigner = async (file: File) => {
      if (!paymentPlan) return;
      const previewRequestId = paymentPlanInstalmentDesignerPreviewRequestId.current + 1;
      paymentPlanInstalmentDesignerPreviewRequestId.current = previewRequestId;
      setIsPaymentPlanInstalmentDesignerPreviewing(true);
      setPaymentPlanInstalmentDesignerPreview(undefined);
      setPaymentPlanInstalmentDesignerPreviewError(undefined);
      return dispatch(previewPaymentPlanInstalmentDesignerCsvThunk({ paymentPlanId: paymentPlan.id, file }))
        .then(unwrapResult)
        .then((preview) => {
          if (paymentPlanInstalmentDesignerPreviewRequestId.current === previewRequestId) {
            setPaymentPlanInstalmentDesignerPreview(preview);
          }
        })
        .catch((error) => {
          if (paymentPlanInstalmentDesignerPreviewRequestId.current === previewRequestId) {
            setPaymentPlanInstalmentDesignerPreviewError(
              (typeof error === 'string' ? error : undefined) ||
                get(error, 'response.data.message', undefined) ||
                get(error, 'message', undefined) ||
                'Could not preview the CSV. Please check the file and try again.',
            );
          }
        })
        .finally(() => {
          if (paymentPlanInstalmentDesignerPreviewRequestId.current === previewRequestId) {
            setIsPaymentPlanInstalmentDesignerPreviewing(false);
          }
        });
    };

    const confirmPaymentPlanInstalmentDesigner = async (file: File) => {
      if (!paymentPlan || !paymentPlanInstalmentDesignerPreview) return;
      setIsPaymentPlanInstalmentDesignerConfirming(true);
      return (
        dispatch(
          applyPaymentPlanInstalmentDesignerCsvThunk({
            claimId: claim.id,
            paymentPlanId: paymentPlan.id,
            file,
            expectedCurrentPaymentPlanTotal: paymentPlanInstalmentDesignerPreview.currentPaymentPlanTotal,
            expectedCurrentScheduleHash: paymentPlanInstalmentDesignerPreview.currentScheduleHash,
          }),
        )
          .then(unwrapResult)
          .then(() => closePaymentPlanInstalmentDesigner())
          // Error flash is dispatched by the thunk; swallow the rejection and keep the modal open to retry.
          .catch(() => undefined)
          .finally(() => setIsPaymentPlanInstalmentDesignerConfirming(false))
      );
    };

    const waiveClaimDisputeFee = async () => {
      setIsFeeBeingWaived(true);
      return dispatch(waiveClaimDisputeFeeThunk({ claimId: claim.id }))
        .then(unwrapResult)
        .then(() => {
          dispatch(setCurrentClaimActionModal(''));
        })
        .finally(() => setIsFeeBeingWaived(false));
    };

    const createADRForm = async () => {
      setIsCreatingADRForm(true);
      return dispatch(generateADRFormThunk({ claimId: claim.id })).then(() => setIsCreatingADRForm(false));
    };

    const createEvidenceChasers = async () => {
      return dispatch(createEvidenceChasersThunk({ claimId: claim.id, shouldCreate: true }));
    };

    const paymentPlanTenant =
      tenants && tenants.find((t) => (paymentPlanCustomer ? t.userId === paymentPlanCustomer.userId : false));
    const paymentPlanTenantName = paymentPlanTenant
      ? `${paymentPlanTenant.firstName} ${paymentPlanTenant.lastName}`.trim()
      : 'Unknown';
    const canIncludeDeletedPaymentPlansInBalance = !!(
      pendingOrderForPaymentPlan &&
      pendingOrderForPaymentPlan.refundHandling === OrderWithOrderCustomersDTORefundHandlingEnum.AUTOMATIC
    );
    const isPaymentPlanDefaulted = Boolean(paymentPlan && paymentPlan.status === PaymentPlanDTOStatusEnum.DEFAULT);
    const unpaidPaymentPlanInstalments =
      paymentPlan && paymentPlan.instalments
        ? paymentPlan.instalments.filter((instalment) => UNPAID_PAYMENT_PLAN_INSTALMENT_STATUSES.includes(instalment.status))
        : [];
    const unpaidPaymentPlanInstalmentsTotal = unpaidPaymentPlanInstalments.reduce(
      (total, instalment) => total + instalment.amount,
      0,
    );
    const canOpenPaymentPlanInstalmentDesigner = Boolean(
      hasPaymentPlanInstalmentDesignerRole &&
        paymentPlan &&
        !NON_DESIGNABLE_PAYMENT_PLAN_STATUSES.includes(paymentPlan.status) &&
        unpaidPaymentPlanInstalments.length,
    );
    const canModifyPaymentPlanInstalments = Boolean(
      hasDeletePaymentPlanInstalmentRole && paymentPlan && paymentPlan.status === PaymentPlanDTOStatusEnum.ACTIVE,
    );
    const canDeletePaymentPlanInstalments = canModifyPaymentPlanInstalments;
    const canReschedulePaymentPlanInstalments = canModifyPaymentPlanInstalments;
    const hasEvidenceRequestButtons =
      isEvidenceReviewButtonVisible() ||
      shouldShowViewExistingEvidenceReviewWhenArbitratedButton() ||
      showShowSkipEvidenceChasersButton() ||
      !!pendingEvidenceChaser;
    const evidenceWorkspaceButton = hasEvidenceWorkspaceRole ? (
      <Button
        onClick={() => {
          dispatch(openEvidenceWorkspaceThunk({ claimId: claim.id }));
        }}
      >
        Evidence workspace
      </Button>
    ) : null;

    return (
      <>
        {isLegacyClaim ? (
          <Container>
            <Row>
              <Col sm={12}>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <BetaBadge>LEGACY CLAIM</BetaBadge>
                </div>
              </Col>
            </Row>
          </Container>
        ) : null}
        <Row>
          <Col lg={12}>
            <Header2>Claim</Header2>
            <Caption>{getDisplayAddress(address)}</Caption>
            <ClaimBadges>
              <ClaimStatus className={status}>{getClaimStatus(claim, isAgentDeciding, isTenantDeciding)}</ClaimStatus>
            </ClaimBadges>
          </Col>
        </Row>
        <Row>
          <Col lg={12}>
            <ActionBar flexStart>
              {isCloseButtonVisible() && (
                <Button onClick={() => dispatch(setCurrentClaimActionModal('close'))} buttonType="warning">
                  Close
                </Button>
              )}
              {isResolveButtonVisible() && (
                <Button onClick={() => dispatch(setCurrentClaimActionModal('resolve'))}>Resolve</Button>
              )}
              <Button onClick={() => dispatch(setCurrentClaimActionModal('setPaidAt'))} disabled={!!claim.supplierPaidAt}>
                Agent Paid
              </Button>
              <DisabledButtonWrapper title={autoChargeDisabledReason}>
                <Button
                  onClick={() => dispatch(setCurrentClaimActionModal('autoCharge'))}
                  disabled={!hasClaimUpdateAutoChargeRole}
                >
                  {autoChargeButtonMessage}
                </Button>
              </DisabledButtonWrapper>
              {isCreateArbitrationButtonVisible() && (
                <Button onClick={() => dispatch(setCurrentClaimActionModal('createArbitration'))}>Create Arbitration</Button>
              )}
              {isWaiveDisputeFeeButtonVisible() ? (
                <Button onClick={() => dispatch(setCurrentClaimActionModal('waiveDisputeFee'))}>Waive Dispute Fee</Button>
              ) : null}
              {isReopenButtonVisible() && (
                <Button onClick={() => dispatch(setCurrentClaimActionModal('reopenClaim'))}>Reopen Claim</Button>
              )}
              {isDeleteButtonVisible() && (
                <DisabledButtonWrapper title={deleteClaimButtonState.tooltip}>
                  <Button
                    onClick={() => dispatch(setCurrentClaimActionModal('deleteClaim'))}
                    buttonType="warning"
                    disabled={deleteClaimButtonState.disabled}
                  >
                    Delete Claim
                  </Button>
                </DisabledButtonWrapper>
              )}
              <Button onClick={() => dispatch(setCurrentClaimActionModal('downloadDocuments'))}>Download Documents</Button>
              {!hasEvidenceRequestButtons ? evidenceWorkspaceButton : null}
              {isADRFormButtonVisible() && (
                <Button disabled={isCreatingADRForm} onClick={() => createADRForm()}>
                  Generate ADR Form
                </Button>
              )}
              {/* Leaving here */}
              {/* <Button
                onClick={() => dispatch(setCurrentClaimActionModal('downloadLocalDocuments'))}
                disabled={!!isBeingDownloaded}
              >
                Download Documents Locally (NEW)
              </Button> */}
              {!paymentPlan &&
                claim.status !== ClaimWithCheckoutRelationsDTOStatusEnum.RESOLVED &&
                !!hasCreatePaymentPlanRole && (
                  <Button onClick={() => dispatch(setCurrentClaimActionModal('createPaymentPlan'))}>Create Payment Plan</Button>
                )}
              {paymentPlan && hasStandardDeletablePaymentPlanStatus && hasDeletePaymentPlanRole && (
                <Button onClick={() => dispatch(setCurrentClaimActionModal('deletePaymentPlan'))}>Delete Payment Plan</Button>
              )}
              {paymentPlan && hasForceDeletablePaymentPlanStatus && hasForceDeletePaymentPlanRole && (
                <Button onClick={() => dispatch(setCurrentClaimActionModal('forceDeletePaymentPlan'))} buttonType="warning">
                  Force Delete Active Payment Plan
                </Button>
              )}
              {/* this only shows when there is an existing evidence review and they can potentially create a new one */}
              {shouldShowViewExistingEvidenceReviewWhenArbitratedButton() ? (
                <Button
                  onClick={() => {
                    dispatch(createEvidenceChasersThunk({ claimId: claim.id, shouldCreate: false }));
                  }}
                  disabled={isCreatingEvidenceChasers}
                >
                  View Existing Evidence Review
                </Button>
              ) : null}
              {isEvidenceReviewButtonVisible() ? (
                <Button
                  onClick={() => {
                    if (shouldCreateNewEvidenceChaser()) {
                      dispatch(setCurrentClaimActionModal('createEvidenceChasers'));
                    } else {
                      dispatch(createEvidenceChasersThunk({ claimId: claim.id, shouldCreate: false }));
                    }
                  }}
                  disabled={isCreatingEvidenceChasers}
                >
                  {shouldCreateNewEvidenceChaser() ? 'Request Evidence' : 'View Evidence Request'}
                </Button>
              ) : null}
              {showShowSkipEvidenceChasersButton() ? (
                <Button onClick={() => dispatch(setCurrentClaimActionModal('skipEvidenceChasers'))}>All Evidence Provided</Button>
              ) : null}
              {pendingEvidenceChaser ? (
                <Button onClick={() => dispatch(setCurrentClaimActionModal('expireEvidenceChaser'))}>
                  Expire Evidence Request
                </Button>
              ) : null}
              {hasEvidenceRequestButtons ? evidenceWorkspaceButton : null}
            </ActionBar>
          </Col>
        </Row>
        <Row>
          <Col sm={12}>
            {globalFlashMessagePayload ? (
              <>
                <br />
                <FlashMessage
                  onDismiss={onDismissGlobalFlashMessage}
                  timeRemaining={FLASH_MESSAGE_TIMEOUT}
                  payload={globalFlashMessagePayload}
                />
              </>
            ) : undefined}
          </Col>
        </Row>
        <Row>
          <Col lg={12}>
            {status === ClaimWithCheckoutRelationsDTOStatusEnum.AWAITINGREVIEW ? (
              <ClaimReview claimId={id} canSendMessage={!repositMessage} />
            ) : null}
          </Col>
        </Row>
        <Row>
          <Col lg={12}>
            <ClaimDetails
              claim={claim}
              onEditBankTransactionsEnabledAt={
                canSetBankTransactionsEnabledAt
                  ? () => dispatch(setCurrentClaimActionModal('bankTransactionsEnabledAt'))
                  : undefined
              }
              editBankTransactionsEnabledAtDisabledReason={getBankTransactionsEnabledAtDisabledReason(
                bankTransactionsEnabledAtEligibility,
              )}
            />
          </Col>
        </Row>
        <Row>
          <Col lg={12}>
            {paymentPlan && paymentPlanTenant ? (
              <PaymentPlan
                paymentPlan={paymentPlan}
                paymentPlanTenant={paymentPlanTenant}
                manualPaymentsTotal={orderManualPaymentsTotal}
                onDeleteInstalment={canDeletePaymentPlanInstalments ? deletePaymentPlanInstalment : undefined}
                deletingInstalmentId={deletingPaymentPlanInstalmentId}
                onPreviewInstalmentReschedule={
                  canReschedulePaymentPlanInstalments ? previewPaymentPlanInstalmentReschedule : undefined
                }
                onRescheduleInstalment={canReschedulePaymentPlanInstalments ? reschedulePaymentPlanInstalment : undefined}
                previewingRescheduleInstalmentId={previewingPaymentPlanInstalmentRescheduleId}
                reschedulingInstalmentId={reschedulingPaymentPlanInstalmentId}
                onOpenInstalmentDesigner={
                  canOpenPaymentPlanInstalmentDesigner ? () => setIsPaymentPlanInstalmentDesignerOpen(true) : undefined
                }
                onToggleTenantDeferral={hasPaymentPlanInstalmentDesignerRole ? toggleTenantDeferral : undefined}
                togglingTenantDeferral={togglingTenantDeferral}
              />
            ) : null}
          </Col>
        </Row>
        <Row>
          <Col lg={12}>
            <ClaimFinancialSummary
              summaryState={claimFinancialSummaryState}
              totalPaid={totalPaidAgainstFinalAmount}
              isTotalPaidLoading={isTotalPaidLoading}
              interestAmount={hasIncludedPaymentPlanInterest ? canonicalPaymentPlanInterestAmount : undefined}
              originalInterestAmount={canonicalOriginalPaymentPlanInterestAmount}
              principalAmountRemaining={canonicalOrderAmountRemaining}
              paymentPlanBalance={canonicalOrderAmountRemainingIncludingInterest}
              isPaymentPlanBalanceLoading={isPaymentPlanBalanceLoading}
            />
          </Col>
        </Row>
        <Row>
          <Col lg={12}>
            <ClaimPayments claimId={id} />
          </Col>
        </Row>
        <Row>
          <Col lg={12}>
            <ClaimManualPayments claimId={id} />
          </Col>
        </Row>
        <Row>
          <Col lg={12}>
            <ClaimStatuses statuses={uniqueStatuses} />
          </Col>
        </Row>
        <Row>
          <Col lg={12}>
            <DeadlinesCard
              title="Claim deadlines"
              claim={claim}
              tenancyOutcomeDeadline={tenancy.outcomeDeadline}
              onExtendResponseDeadline={
                isExtendResponseDeadlineButtonVisible()
                  ? () => dispatch(setCurrentClaimActionModal('extendResponseDeadline'))
                  : undefined
              }
            />
          </Col>
        </Row>
        <Row>
          <Col lg={12}>
            {tenancyOrder && (
              <RepositSummaryCard
                landlord={landlord}
                notes={repositNotes}
                notesError={repositNotesError}
                tenancy={tenancy}
                tenancyOrder={tenancyOrder}
                tenantsWithActions={tenantsWithActions}
              />
            )}
          </Col>
        </Row>
        <Row>
          <Col lg={12}>
            {mediationEnabled ? (
              <MediationClaimItems
                claimItems={activeMediationItems}
                proposals={proposals}
                onEditClaimItem={handleOpenEditModal}
                isUpdatingAmount={isUpdatingClaimItemAmount || isPreviewingClaimItemUpdate}
                isEditDisabled={isClaimResolved || !hasClaimItemCorrectiveChangeRole}
                editDisabledReason={claimItemEditDisabledReason}
              />
            ) : (
              <ClaimItems
                title="Active Claim Items"
                showDeletedAt={false}
                items={activeItems}
                proposals={proposals}
                onEditClaimItem={handleOpenEditModal}
                isUpdatingAmount={isUpdatingClaimItemAmount || isPreviewingClaimItemUpdate}
                isEditDisabled={isClaimResolved || !hasClaimItemCorrectiveChangeRole}
                editDisabledReason={claimItemEditDisabledReason}
              />
            )}
          </Col>
        </Row>
        {!!deletedItems.length && (
          <Row>
            <Col lg={12}>
              <ClaimItems title="Deleted Claim Items" showDeletedAt={true} items={deletedItems} proposals={proposals} />
            </Col>
          </Row>
        )}
        <Row>
          <Col lg={12}>
            <ClaimArbitration claim={claim} />
          </Col>
        </Row>
        {details && (
          <Row>
            <Col lg={12}>
              <RepositCard title="Claim Description" tooltip="lorem">
                <P1>{details}</P1>
              </RepositCard>
            </Col>
          </Row>
        )}
        {notes && (
          <Row>
            <Col lg={12}>
              <RepositCard title="Notes" tooltip="lorem">
                <P1>{notes}</P1>
              </RepositCard>
            </Col>
          </Row>
        )}
        {repositMessage && (
          <Row>
            <Col lg={12}>
              <RepositCard title="Reposit Messages" tooltip="lorem">
                <P1>{repositMessage}</P1>
              </RepositCard>
            </Col>
          </Row>
        )}
        {agentResponse && (
          <Row>
            <Col lg={12}>
              <RepositCard title="Agent Response" tooltip="lorem">
                <P1>{agentResponse}</P1>
              </RepositCard>
            </Col>
          </Row>
        )}
        {tenancyInventory ? <InventoryDetails inventory={tenancyInventory} /> : null}
        {documents && documents.length > 0 && (
          <Row>
            <Col lg={12}>
              <ClaimDocuments documents={documents} />
            </Col>
          </Row>
        )}
        {claim && claim.tenantDisputeMessage && (
          <Row>
            <Col lg={12}>
              <RepositCard flush title="Dispute Information">
                <DisputeInformation claim={claim} />
              </RepositCard>
            </Col>
          </Row>
        )}

        {renderStandardConfirmationModal()}
        {currentClaimModal === 'setPaidAt' && (
          <Modal onDismiss={() => dispatch(setCurrentClaimActionModal(''))}>
            <Header3 style={{ marginBottom: 6 }}>Please, select the payment date</Header3>
            <Formik initialValues={{ paidAt: undefined }} onSubmit={handleSetPaid}>
              {(props) => (
                <>
                  <FieldWithLabel label="Paid at" style={{ marginBottom: 24 }}>
                    <DatePicker
                      name="paidAt"
                      value={props.values.paidAt}
                      onBlur={props.handleBlur}
                      dateFormat={DATE_FORMAT}
                      placeholder={'DD/MM/YYYY'}
                      maxDate={new Date()}
                      onChange={(date) => props.setFieldValue('paidAt', date)}
                    />
                  </FieldWithLabel>
                  <ActionBar>
                    <Button onClick={props.handleSubmit} disabled={!props.values.paidAt || loadingSetPaidAt}>
                      Confirm
                    </Button>
                  </ActionBar>
                </>
              )}
            </Formik>
          </Modal>
        )}
        {currentClaimModal === 'bankTransactionsEnabledAt' && canSetBankTransactionsEnabledAt ? (
          <EditBankTransactionsEnabledAtModal claimId={id} onDismiss={() => dispatch(setCurrentClaimActionModal(''))} />
        ) : null}
        {currentClaimModal === 'createArbitration' && (
          <Modal onDismiss={() => dispatch(setCurrentClaimActionModal(''))}>
            {createArbitrationFlashMessagePayload ? (
              <FlashMessage
                payload={createArbitrationFlashMessagePayload}
                onDismiss={onDismissCreateArbitrationFlashMessage}
                timeRemaining={FLASH_MESSAGE_TIMEOUT}
              />
            ) : null}
            <Header3 style={{ marginBottom: 6 }}>Create arbitration</Header3>
            <Formik
              validationSchema={ARBITRATION_VALIDATION_SCHEMA}
              initialValues={{ arbitratedAmount: undefined }}
              onSubmit={handleCreateArbitration}
            >
              {({ errors, touched, values, isValid, handleChange, handleBlur, handleSubmit }) => (
                <>
                  <FieldWithLabel label="Amount" error={errors.arbitratedAmount} touched={touched.arbitratedAmount}>
                    <Input
                      name="arbitratedAmount"
                      type="number"
                      value={`${values.arbitratedAmount}`}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      icon={poundIcon}
                      error={errors.arbitratedAmount}
                      touched={touched.arbitratedAmount}
                    />
                  </FieldWithLabel>
                  <br />
                  <FieldWithLabel label="Summary Document">
                    <UploaderContainer>
                      <BrowseLabel htmlFor="arbitrationDocument">Select a file</BrowseLabel>
                      <UploaderDocumentName>{arbitrationFile && arbitrationFile.name}</UploaderDocumentName>
                    </UploaderContainer>
                    <InputFile id="arbitrationDocument" name="arbitrationDocument" onChange={handleFileChange} />
                  </FieldWithLabel>
                  <ActionBar>
                    <Button onClick={handleSubmit} disabled={loadingCreateArbitration || !isValid || !arbitrationFile}>
                      Confirm
                    </Button>
                  </ActionBar>
                </>
              )}
            </Formik>
          </Modal>
        )}

        {currentClaimModal === 'waiveDisputeFee' ? (
          <WaiveClaimDisputeFeeModal
            isSubmitting={isFeeBeingWaived}
            onDismiss={() => dispatch(setCurrentClaimActionModal(''))}
            onSubmit={waiveClaimDisputeFee}
          />
        ) : null}

        {currentClaimModal === 'reopenClaim' && (
          <ReopenModal
            type={ReopenType.CLAIM}
            onSubmit={(date) =>
              dispatch(reopenClaimApiRequested({ claimId: claim.id, outcomeDeadline: moment(date).format('YYYY-MM-DD') }))
            }
            onDismiss={() => dispatch(setCurrentClaimActionModal(''))}
            isSubmitting={isSubmittingReopen}
          />
        )}

        {currentClaimModal === 'extendResponseDeadline' && (
          <ExtendResponseDeadlineModal
            onPreview={(responseDeadline) =>
              dispatch(previewResponseDeadlineExtensionThunk({ claimId: claim.id, responseDeadline })).then(unwrapResult)
            }
            onConfirm={(values) =>
              // On failure the modal stays open and shows the error.
              dispatch(extendResponseDeadlineThunk({ claimId: claim.id, ...values }))
                .then(unwrapResult)
                .then(() => {
                  dispatch(setCurrentClaimActionModal(''));
                })
                .catch(() => undefined)
            }
            onDismiss={() => dispatch(setCurrentClaimActionModal(''))}
          />
        )}

        {/* Leaving here */}
        {/* {currentClaimModal === 'downloadLocalDocuments' && (
          <Modal onDismiss={() => dispatch(setCurrentClaimActionModal(''))}>
            <Header3>Download Documents Locally!</Header3>
            <P2>Are you sure you would like to download all of the documents attached to this Claim?</P2>
            <ActionBar>
              <Button onClick={downloadDocumentsLocal}>Confirm</Button>
            </ActionBar>
          </Modal>
        )} */}

        {currentClaimModal === 'createPaymentPlan' ? (
          // pass in order customers for correct order instead of claim respondents
          <PaymentPlanModal
            onDismiss={() => dispatch(setCurrentClaimActionModal(''))}
            totalBalance={ppOrderAmountRemaining || 0}
            orderCustomers={ppOrderCustomers}
            tenants={tenants}
            onSubmit={createPaymentPlan}
            isSubmitting={isPPSubmitting}
            showDeletedPaymentPlansInBalanceNotice={canIncludeDeletedPaymentPlansInBalance}
            isClaimProposalPaymentPlanBlocked={isClaimProposalPaymentPlanBlocked}
            isAmountOwedUnfinalised={isPaymentPlanBlockedUntilAmountFinalised}
          />
        ) : null}

        {currentClaimModal === 'deletePaymentPlan' ? (
          <DeletePaymentPlanModal
            onDismiss={() => dispatch(setCurrentClaimActionModal(''))}
            onSubmit={deletePaymentPlan}
            isSubmitting={isDeletePPSubmitting}
          />
        ) : null}

        {currentClaimModal === 'forceDeletePaymentPlan' ? (
          <DeletePaymentPlanModal
            onDismiss={() => dispatch(setCurrentClaimActionModal(''))}
            onSubmit={forceDeletePaymentPlan}
            isSubmitting={isDeletePPSubmitting}
            isForceDelete={true}
          />
        ) : null}

        {paymentPlan && isPaymentPlanInstalmentDesignerOpen && canOpenPaymentPlanInstalmentDesigner ? (
          <PaymentPlanInstalmentDesignerModal
            address={claimAddressLine}
            tenantName={paymentPlanTenantName}
            currentUnpaidInstalmentCount={unpaidPaymentPlanInstalments.length}
            currentUnpaidTotal={unpaidPaymentPlanInstalmentsTotal}
            currentPaymentPlanTotal={paymentPlan.amount}
            reinstatesPaymentPlan={isPaymentPlanDefaulted}
            selectedFile={paymentPlanInstalmentDesignerFile}
            preview={paymentPlanInstalmentDesignerPreview}
            previewError={paymentPlanInstalmentDesignerPreviewError}
            isPreviewing={isPaymentPlanInstalmentDesignerPreviewing}
            isConfirming={isPaymentPlanInstalmentDesignerConfirming}
            onDownload={() => dispatch(downloadPaymentPlanInstalmentDesignerCsvThunk({ paymentPlanId: paymentPlan.id }))}
            onPreview={previewPaymentPlanInstalmentDesigner}
            onFileChange={resetPaymentPlanInstalmentDesignerPreview}
            onSelectedFileChange={setPaymentPlanInstalmentDesignerFile}
            onConfirm={confirmPaymentPlanInstalmentDesigner}
            onGoBack={resetPaymentPlanInstalmentDesignerPreview}
            onDismiss={closePaymentPlanInstalmentDesigner}
          />
        ) : null}

        {currentClaimModal === 'deleteClaim' ? (
          <DeleteClaimModal
            onDismiss={() => dispatch(setCurrentClaimActionModal(''))}
            onSubmit={() => dispatch(deleteClaimRequested({ claimId: claim.id }))}
            isSubmitting={isDeletingClaim}
            isPreviewLoading={isPreviewingDeleteClaim}
            preview={claimDeletionPreview}
            claimId={claim.id}
            claimAddress={claimAddressLine}
            claimItems={claim.items}
          />
        ) : null}

        {currentClaimModal === 'downloadDocuments' ? (
          <DownloadClaimDocumentsModal
            onDismiss={() => dispatch(setCurrentClaimActionModal(''))}
            onSubmit={(type) => dispatch(downloadDocumentsApiRequested({ claimId: claim.id, type }))}
            isSubmitting={isDownloadingClaimDocuments}
          />
        ) : null}

        {currentClaimModal === 'createEvidenceChasers' ? (
          <CreateEvidenceChasersModal
            onDismiss={() => dispatch(setCurrentClaimActionModal(''))}
            onSubmit={createEvidenceChasers}
            isSubmitting={isCreatingEvidenceChasers}
          />
        ) : null}

        {editingClaimItem || pendingClaimItemPreview ? (
          <ClaimItemEditModal
            claimItemId={editingClaimItem ? editingClaimItem.id : pendingClaimItemPreview!.payload.claimItemId}
            currentAmount={editingClaimItem ? editingClaimItem.currentAmount : pendingClaimItemPreview!.preview.currentItemAmount}
            title={editingClaimItem ? editingClaimItem.title : undefined}
            itemType={editingClaimItem ? editingClaimItem.itemType : undefined}
            claimSummary={{ id: claim.id, address: claimAddressLine, status: claim.status }}
            pendingPreview={pendingClaimItemPreview}
            isPreviewing={isPreviewingClaimItemUpdate}
            isConfirming={isUpdatingClaimItemAmount}
            isEditDisabled={isClaimResolved || !hasClaimItemCorrectiveChangeRole}
            onReduce={handleUpdateClaimItemAmount}
            onRemove={(claimItemId) => handleUpdateClaimItemAmount(claimItemId, 0)}
            onConfirm={handleConfirmClaimItemUpdate}
            onGoBack={() => dispatch(clearClaimItemPreview())}
            onDismiss={handleDismissClaimItemModal}
          />
        ) : null}
      </>
    );
  };

  return (
    <Container style={{ marginBottom: 120 }}>
      {/* TODO: NEED TO ADD LOADING FOR ppOrderAmountRemaining here */}
      {!isFetchClaimLoading && hasClaimFetched && claim && claim.checkout ? (
        renderClaimDetails(claim, isAgentDeciding, isTenantDeciding)
      ) : (
        <Loading />
      )}
    </Container>
  );
};

export default ClaimView;
